import { useState, useEffect, useRef } from "react";

// ─── Setup required ──────────────────────────────────────────────────────────
// 1. tailwind.config.js → extend fontFamily:
//      syne: ['Syne', 'sans-serif'], manrope: ['Manrope', 'sans-serif']
// 2. index.html <head>:
//      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>
// 3. App.js route (already in your file, just add the import):
//      import Landing from './pages/Landing';
//      <Route path='/Landing' element={<Landing handleAuth={handleAuth} email={email} setEmail={setEmail}
//        setPassword={setPassword} isSignUp={isSignUp} setIsSignUp={setIsSignUp} isSigned={isSigned} />} />
// ─────────────────────────────────────────────────────────────────────────────

// ── STATIC DATA ───────────────────────────────────────────────────────────────

const TICKER_ITEMS = [
  "🟢 New student vendor joined · 2 min ago",
  "📦 Order placed in Port Harcourt · just now",
  "💰 Vendor earned NGN 12,400 this week",
  "⭐ New 5-star review · 8 min ago",
  "🚀 218 products live and growing",
  "🎓 UNIPORT student made first sale · 1 hr ago",
  "🛍️ Fashion vendor joined from RSU · 4 min ago",
  "📲 WhatsApp order confirmed · just now",
];

const WHY_CARDS = [
  { icon: "🆓", title: "100% Free — Forever",          highlight: "NGN 0 to get started",         body: "No listing fees. No monthly charges. No hidden costs. List unlimited products free and only pay a tiny commission when you actually sell." },
  { icon: "👥", title: "400+ Buyers Ready Now",         highlight: "Real buyers from Day 1",        body: "Walk into a marketplace with buyers already waiting. Our WhatsApp community of 400+ active university students sees your products every week." },
  { icon: "🛒", title: "Full Checkout — Like Jumia",    highlight: "Buyers pay directly on site",   body: "Buyers browse, add to cart, and pay directly on uCart — no redirects. A complete e-commerce experience, completely free for sellers." },
  { icon: "💬", title: "WhatsApp-First Support",        highlight: "Built for Nigerian habits",     body: "Every order connects buyer and seller via WhatsApp. The way Nigerians actually communicate — personal, fast, and trustworthy." },
  { icon: "🛡️", title: "Buyer Protection = More Sales", highlight: "3x higher conversion rate",    body: "Our buyer protection badge removes purchase hesitation. When buyers trust the platform, they trust your listings — and buy more." },
  { icon: "📣", title: "We Market You For Free",        highlight: "Free weekly promotion",         body: "Every week we feature vendor products on our TikTok, Instagram, and 400-member WhatsApp group. Your listings get promoted at zero cost." },
];

const STEPS = [
  { num: "01", title: "Create your free account below",   time: "Takes 2 minutes",                body: "Enter your email and password. No business registration, no ID upload, no verification delays. Just sign up and you're in." },
  { num: "02", title: "Complete your seller profile",     time: "Done in under 5 minutes",        body: "After signup you'll land on your Profile page. Add your store name, WhatsApp number, and what you sell. This is how buyers find you." },
  { num: "03", title: "We build and promote your products",  time: "Live within 24 hours",           body: "Our team helps upload your first products and features them in our 400-member WhatsApp group and social media in your first week." },
  { num: "04", title: "Buyers checkout. You get paid.",   time: "Payment on delivery or transfer", body: "Students browse, add to cart, and pay directly on uCart. Orders notify you instantly via WhatsApp. Deliver and get paid." },
];

const COMPARE_ROWS = [
  { feature: "Free to list products",              ucart: "✓", jumia: "✗",       jiji: "Partial" },
  { feature: "Full site checkout (like Jumia)",    ucart: "✓", jumia: "✓",       jiji: "✗"       },
  { feature: "Free for sellers forever",           ucart: "✓", jumia: "✗",       jiji: "✗"       },
  { feature: "Student / university audience",      ucart: "✓", jumia: "✗",       jiji: "✗"       },
  { feature: "WhatsApp order communication",       ucart: "✓", jumia: "✗",       jiji: "✗"       },
  { feature: "Buyer protection on every order",    ucart: "✓", jumia: "✓",       jiji: "✗"       },
  { feature: "Free weekly product promotion",      ucart: "✓", jumia: "✗",       jiji: "✗"       },
  { feature: "Personal founder support",           ucart: "✓", jumia: "✗",       jiji: "✗"       },
  { feature: "Setup time",                         ucart: "5 min", jumia: "Days", jiji: "1 hr"   },
];

const TESTIMONIALS = [
  { initial: "A", name: "Adaeze O.",  role: "Fashion accessories · Port Harcourt", from: "from-emerald-500 to-green-700",  text: "\"Listed on uCart and within the first week they featured me in the WhatsApp group. Got 3 unexpected orders. Most supportive marketplace I've been on.\"" },
  { initial: "K", name: "Kingsley M.", role: "Electronics · UNIPORT student",      from: "from-green-600 to-emerald-800",  text: "\"Was on other platforms but scammer buyers were too much. On uCart buyers go through proper checkout — I actually get paid. The founder replies personally too.\"" },
  { initial: "F", name: "Fatima B.",  role: "Skincare vendor · Rivers State Uni",  from: "from-teal-500 to-green-700",     text: "\"Thought I needed money to sell online. uCart proved me wrong. Free listing, free promotion, full checkout experience. Set up in one afternoon.\"" },
];

const FAQS = [
  { q: "Is uCart really free for sellers?",           a: "Yes — 100% free forever. List unlimited products at no cost. uCart only takes a tiny commission when you make a sale. No monthly fees, no listing charges, no hidden costs ever." },
  { q: "How do buyers pay on uCart?",                  a: "Just like Jumia — buyers browse, add to cart, and pay directly on the uCart website via bank transfer. Full e-commerce checkout built in. No external redirects." },
  { q: "What can I sell on uCart?",                    a: "Fashion, accessories, electronics, gadgets, skincare, food, home goods, school supplies — anything legal that university students and young adults want to buy." },
  { q: "Do I need to be a registered business?",       a: "No. Many vendors are students selling from their rooms. You just need an email, a WhatsApp number, and products to sell. No CAC registration required to start." },
  { q: "How quickly will I get my first sale?",        a: "Every new vendor gets featured in our 400-member WhatsApp group and social media in their first week. Most vendors receive their first enquiry within 48 hours of going live." },
  { q: "What if a buyer has a complaint?",             a: "uCart handles all buyer disputes. As long as you have delivery confirmation, you are protected. We investigate fairly — sellers are never automatically at fault." },
];

// Nigerian banks list
const NIGERIAN_BANKS = [
  { name: "Access Bank", code: "044" },
  { name: "Citibank Nigeria", code: "023" },
  { name: "Ecobank Nigeria", code: "050" },
  { name: "Fidelity Bank", code: "070" },
  { name: "First Bank of Nigeria", code: "011" },
  { name: "First City Monument Bank (FCMB)", code: "214" },
  { name: "Guaranty Trust Bank (GTBank)", code: "058" },
  { name: "Heritage Bank", code: "030" },
  { name: "Keystone Bank", code: "082" },
  { name: "Kuda Bank", code: "090267" },
  { name: "Opay", code: "999992" },
  { name: "Palmpay", code: "999991" },
  { name: "Polaris Bank", code: "076" },
  { name: "Providus Bank", code: "101" },
  { name: "Stanbic IBTC Bank", code: "221" },
  { name: "Sterling Bank", code: "232" },
  { name: "UBA (United Bank for Africa)", code: "033" },
  { name: "Union Bank of Nigeria", code: "032" },
  { name: "Unity Bank", code: "215" },
  { name: "VFD Microfinance Bank", code: "566" },
  { name: "Wema Bank", code: "035" },
  { name: "Zenith Bank", code: "057" },
];

// ── HELPERS ───────────────────────────────────────────────────────────────────

function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function RevealDiv({ children, className = "", delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {children}
    </div>
  );
}

function SectionTag({ children, center = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-emerald-400 text-[11px] font-bold tracking-[0.12em] uppercase mb-4 ${center ? "justify-center w-full" : ""}`}>
      <span className="w-4 h-[1.5px] bg-emerald-400 rounded" />
      {children}
    </div>
  );
}

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────

function Ticker() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="w-full overflow-hidden border-y border-emerald-500/15 bg-emerald-500/5 py-3.5">
      <div
        className="flex gap-16 w-max"
        style={{ animation: "ticker 30s linear infinite" }}
      >
        {doubled.map((item, i) => (
          <span key={i} className="whitespace-nowrap text-[13px] font-semibold text-white/60">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function WhyCard({ card, delay }) {
  return (
    <RevealDiv
      delay={delay}
      className="group relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-8
                 hover:bg-emerald-500/[0.06] hover:border-emerald-500/25
                 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="w-12 h-12 rounded-[14px] bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-2xl mb-5">
        {card.icon}
      </div>
      <h3 className="font-syne text-[19px] font-bold text-white mb-2">{card.title}</h3>
      <p className="text-[14px] text-white/50 leading-relaxed mb-4">{card.body}</p>
      <span className="text-[13px] font-bold text-emerald-400">↗ {card.highlight}</span>
    </RevealDiv>
  );
}

function Step({ step, index }) {
  const [ref, visible] = useReveal(0.2);
  return (
    <div
      ref={ref}
      className={`grid grid-cols-[72px_1fr] gap-6 py-10 border-b border-emerald-500/10 last:border-0
                  transition-all duration-700 ease-out
                  ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: visible ? `${index * 120}ms` : "0ms" }}
    >
      <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-[1.5px] border-emerald-500/30
                      flex items-center justify-center font-syne text-[20px] font-extrabold text-emerald-400 flex-shrink-0">
        {step.num}
      </div>
      <div>
        <h3 className="font-syne text-[21px] font-bold text-white mb-2">{step.title}</h3>
        <p className="text-[15px] text-white/50 leading-relaxed">{step.body}</p>
        <div className="inline-flex items-center gap-2 mt-3 text-[12px] font-semibold text-yellow-400 tracking-wide">
          ⏱ {step.time}
        </div>
      </div>
    </div>
  );
}

function CompareTable() {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`max-w-4xl mx-auto rounded-2xl overflow-hidden border border-emerald-500/15
                  transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
    >
      {/* Header row */}
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr] bg-emerald-500/10 border-b border-emerald-500/15 px-6 py-4">
        <span className="font-syne font-bold text-[14px] text-white/80">Feature</span>
        <span className="font-syne font-bold text-[15px] text-emerald-400 text-center">uCart ✓</span>
        <span className="font-syne font-bold text-[13px] text-white/35 text-center">Jumia</span>
        <span className="font-syne font-bold text-[13px] text-white/35 text-center">Jiji</span>
      </div>
      {COMPARE_ROWS.map((row, i) => (
        <div
          key={i}
          className={`grid grid-cols-[2fr_1fr_1fr_1fr] px-6 py-[17px]
                      border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors
                      ${i % 2 === 1 ? "bg-white/[0.01]" : ""}`}
        >
          <span className="text-[14px] text-white/70 font-medium">{row.feature}</span>
          <span className="text-center text-[17px] font-bold text-emerald-400">{row.ucart}</span>
          <span className={`text-center text-[15px] font-semibold ${row.jumia === "✓" ? "text-white/35" : "text-white/20"}`}>{row.jumia}</span>
          <span className={`text-center text-[15px] font-semibold ${row.jiji === "✓" ? "text-white/35" : "text-white/20"}`}>{row.jiji}</span>
        </div>
      ))}
    </div>
  );
}

function TestiCard({ t, delay }) {
  return (
    <RevealDiv delay={delay} className="relative bg-white/[0.03] border border-white/[0.07] rounded-2xl p-8">
      <div className="absolute top-5 right-6 font-syne text-[80px] leading-none text-emerald-500/10 font-extrabold select-none">"</div>
      <div className="text-yellow-400 text-[13px] tracking-[3px] mb-4">★★★★★</div>
      <p className="text-[15px] text-white/65 leading-relaxed mb-6">{t.text}</p>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${t.from} flex items-center justify-center font-syne font-bold text-[17px] text-white flex-shrink-0`}>
          {t.initial}
        </div>
        <div>
          <div className="font-bold text-[14px] text-white">{t.name}</div>
          <div className="text-[12px] text-emerald-500 mt-0.5">{t.role}</div>
        </div>
      </div>
    </RevealDiv>
  );
}

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-emerald-500/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-6 flex items-center justify-between gap-4 group"
      >
        <span className="font-semibold text-[16px] text-white group-hover:text-emerald-400 transition-colors">
          {faq.q}
        </span>
        <span className={`w-7 h-7 flex-shrink-0 rounded-full bg-emerald-500/10 border border-emerald-500/20
                         flex items-center justify-center text-emerald-400 text-[18px]
                         transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
          +
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? "max-h-48 pb-5" : "max-h-0"}`}>
        <p className="text-[14px] text-white/55 leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

// ── ACCOUNT VERIFICATION MODAL ────────────────────────────────────────────────

function AccountVerifyModal({ isOpen, onClose, onConfirm, accountName, isVerifying, verifyError, bankName, accountNumber }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(0,0,0,0.7)" }}>
      <div
        className="relative w-full max-w-[400px] bg-[#0d1410] border border-emerald-500/25 rounded-2xl p-8 shadow-[0_0_80px_rgba(16,185,129,0.12)]"
        style={{ animation: "popIn 0.3s ease both" }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all text-[18px]"
        >
          ×
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-[12px] bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-xl">
            🏦
          </div>
          <div>
            <h3 className="font-syne font-bold text-[17px] text-white">Verify Bank Account</h3>
            <p className="text-[12px] text-white/40">Confirm your payout details</p>
          </div>
        </div>

        {/* Details summary */}
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4 mb-5 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-[12px] text-white/40 uppercase tracking-[0.06em] font-semibold">Bank</span>
            <span className="text-[14px] text-white font-medium">{bankName}</span>
          </div>
          <div className="h-px bg-white/5" />
          <div className="flex justify-between items-center">
            <span className="text-[12px] text-white/40 uppercase tracking-[0.06em] font-semibold">Account No.</span>
            <span className="text-[14px] text-white font-medium tracking-widest">{accountNumber}</span>
          </div>
        </div>

        {/* Verification state */}
        {isVerifying ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <svg className="w-8 h-8 text-emerald-400 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-[14px] text-white/50">Verifying account details…</p>
          </div>
        ) : verifyError ? (
          <div className="bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 mb-5 flex items-start gap-3">
            <span className="text-red-400 text-[18px] mt-0.5 flex-shrink-0">⚠</span>
            <div>
              <p className="text-[13px] text-red-300 font-semibold mb-0.5">Verification failed</p>
              <p className="text-[12px] text-red-400/70">{verifyError}</p>
            </div>
          </div>
        ) : accountName ? (
          <div className="bg-emerald-500/8 border border-emerald-500/25 rounded-xl px-4 py-3.5 mb-5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-[11px] text-emerald-400/70 uppercase tracking-[0.08em] font-bold mb-0.5">Account Name</p>
              <p className="text-[16px] font-bold text-white">{accountName}</p>
            </div>
          </div>
        ) : null}

        {/* Action buttons */}
        <div className="flex gap-3 mt-2">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-white/50 hover:text-white hover:border-white/25 text-[14px] font-semibold transition-all"
          >
            Edit Details
          </button>
          {accountName && !verifyError && (
            <button
              onClick={onConfirm}
              className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[14px] font-bold font-syne transition-all hover:-translate-y-[1px] shadow-[0_0_30px_rgba(16,185,129,0.25)]"
            >
              Confirm & Sign Up
            </button>
          )}
          {verifyError && (
            <button
              onClick={onConfirm}
              className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 text-[14px] font-semibold transition-all"
            >
              Skip & Continue
            </button>
          )}
        </div>

        {accountName && !verifyError && (
          <p className="text-center text-[11px] text-white/25 mt-4">
            Make sure this is correct — payouts will be sent to this account.
          </p>
        )}
      </div>
    </div>
  );
}

// ── SELLER SIGNUP FORM ────────────────────────────────────────────────────────
// Wired to Firebase via handleAuth prop from App.js
// Role is hardcoded as "seller" — profile is created automatically on signup
// Account details are collected here and verified before submission
// On success → App.js handleAuth navigates to /Profile on success

function SellerSignupForm({ handleAuth, email, setEmail, setPassword, setIsSignUp }) {
  const [localPassword, setLocalPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [storeName, setStoreName]             = useState("");
  const [whatsapp, setWhatsapp]               = useState("");
  const [category, setCategory]               = useState("");
  const [bankName, setBankName]               = useState("");
  const [bankCode, setBankCode]               = useState("");
  const [accountNumber, setAccountNumber]     = useState("");
  const [accountName, setAccountName]         = useState("");
  const [termsAccepted, setTermsAccepted]     = useState(false);

  const [fieldError, setFieldError]           = useState("");
  const [shake, setShake]                     = useState(false);
  const [loading, setLoading]                 = useState(false);

  // Modal state
  const [showModal, setShowModal]             = useState(false);
  const [isVerifying, setIsVerifying]         = useState(false);
  const [verifyError, setVerifyError]         = useState("");
  const [verifiedAccountName, setVerifiedAccountName] = useState("");

  // Pending submit state — set after modal confirm
  const [pendingSubmit, setPendingSubmit]     = useState(false);

  const CATEGORIES = [
    "Fashion & Clothing", "Electronics & Gadgets", "Skincare & Beauty",
    "Food & Groceries", "Home & Living", "School Supplies",
    "Accessories & Jewellery", "Other",
  ];

  const triggerShake = (msg) => {
    setFieldError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  // ── Verify account via Paystack ─────────────────────────────────────────────
  // NOTE: Replace PAYSTACK_PUBLIC_KEY with your actual Paystack public key
  // The endpoint used is: https://api.paystack.co/bank/resolve
  // This call uses your Paystack public key (safe for frontend use)
  const verifyAccount = async (accNumber, bCode) => {
    setIsVerifying(true);
    setVerifyError("");
    setVerifiedAccountName("");

    try {
      // ── IMPORTANT: Replace with your actual Paystack Public Key ────────────
      const PAYSTACK_PUBLIC_KEY = "pk_live_87b3bcae331696f80f33222a51b75ba1305c3046";
      // ─────────────────────────────────────────────────────────────────────

      const res = await fetch(
        `https://api.paystack.co/bank/resolve?account_number=${accNumber}&bank_code=${bCode}`,
        {
          headers: {
            Authorization: `Bearer ${PAYSTACK_PUBLIC_KEY}`,
          },
        }
      );
      const data = await res.json();

      if (data.status && data.data?.account_name) {
        setVerifiedAccountName(data.data.account_name);
        setAccountName(data.data.account_name);
      } else {
        setVerifyError(data.message || "Could not verify account. Please check the details and try again.");
      }
    } catch (err) {
      setVerifyError("Network error. Check your connection and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Called when user clicks the main CTA ────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!email.trim())            return triggerShake("Please enter your email address.");
    if (!storeName.trim())        return triggerShake("Please enter your store name.");
    if (!whatsapp.trim())         return triggerShake("Please enter your WhatsApp number.");
    if (!category)                return triggerShake("Please select what you sell.");
    if (!bankName)                return triggerShake("Please select your bank.");
    if (accountNumber.length < 10) return triggerShake("Please enter a valid 10-digit account number.");
    if (localPassword.length < 6) return triggerShake("Password must be at least 6 characters.");
    if (localPassword !== confirmPassword) return triggerShake("Passwords do not match.");
    if (!termsAccepted)           return triggerShake("Please accept the Terms & Conditions to continue.");

    setFieldError("");

    // Open the modal and trigger verification
    setShowModal(true);
    await verifyAccount(accountNumber, bankCode);
  };

  // ── Called when user confirms in the modal ──────────────────────────────────
  const handleModalConfirm = async () => {
    setShowModal(false);
    setLoading(true);

    // Hardcode role as seller; profile page will read this from localStorage/Firestore
    // Store extra seller data in localStorage so Profile.jsx can pre-fill it
    localStorage.setItem("ucart_pending_seller", JSON.stringify({
      storeName,
      whatsapp,
      category,
      bankName,
      bankCode,
      accountNumber,
      accountName: verifiedAccountName || accountName,
      role: "seller",
    }));

    setIsSignUp(true);
    setPassword(localPassword);

    await new Promise((r) => setTimeout(r, 50));

    try {
      await handleAuth();
    } catch {
      // Errors handled by toast in App.js
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-[14px] " +
    "outline-none focus:border-emerald-400 focus:bg-emerald-500/5 transition-all " +
    "placeholder-white/20 appearance-none";

  const selectedBankName = NIGERIAN_BANKS.find(b => b.code === bankCode)?.name || "";

  return (
    <>
      <AccountVerifyModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        accountName={verifiedAccountName}
        isVerifying={isVerifying}
        verifyError={verifyError}
        bankName={selectedBankName}
        accountNumber={accountNumber}
      />

      <div className={`transition-transform ${shake ? "animate-[shake_0.4s_ease]" : ""}`}>

        {/* Store name */}
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            Store / Brand Name *
          </label>
          <input
            className={inputClass}
            placeholder="e.g. Chidi's Gadgets"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            Email Address *
          </label>
          <input
            className={inputClass}
            placeholder="you@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* WhatsApp */}
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            WhatsApp Number *
          </label>
          <input
            className={inputClass}
            placeholder="080XXXXXXXX"
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
          />
        </div>

        {/* Category */}
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            What do you sell? *
          </label>
          <select
            className={inputClass + " cursor-pointer"}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="" disabled>Select a category</option>
            {CATEGORIES.map((c) => (
              <option key={c} className="bg-[#0A0F0D]">{c}</option>
            ))}
          </select>
        </div>

        {/* ── Bank Account Details ─────────────────────────────────────── */}
        <div className="mt-5 mb-3">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 h-px bg-emerald-500/15" />
            <span className="text-[11px] font-bold text-emerald-400/70 uppercase tracking-[0.1em] px-2">
              Payout Details
            </span>
            <div className="flex-1 h-px bg-emerald-500/15" />
          </div>
          <p className="text-[12px] text-white/35 mb-4 leading-relaxed">
            Add your bank account so you can receive payments when buyers order from you.
          </p>
        </div>

        {/* Bank selector */}
        <div className="mb-3">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            Bank Name *
          </label>
          <select
            className={inputClass + " cursor-pointer"}
            value={bankCode}
            onChange={(e) => {
              const selectedBank = NIGERIAN_BANKS.find(b => b.code === e.target.value);
              setBankCode(e.target.value);
              setBankName(selectedBank?.name || "");
              // Reset verification when bank changes
              setVerifiedAccountName("");
              setAccountName("");
            }}
          >
            <option value="" disabled>Select your bank</option>
            {NIGERIAN_BANKS.map((bank) => (
              <option key={bank.code} value={bank.code} className="bg-[#0A0F0D]">
                {bank.name}
              </option>
            ))}
          </select>
        </div>

        {/* Account number */}
        <div className="mb-5">
          <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
            Account Number *
          </label>
          <input
            className={inputClass}
            placeholder="10-digit account number"
            type="number"
            maxLength={10}
            value={accountNumber}
            onChange={(e) => {
              const val = e.target.value.slice(0, 10);
              setAccountNumber(val);
              // Reset verification when account number changes
              setVerifiedAccountName("");
              setAccountName("");
            }}
          />
        </div>

        {/* Password row */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
              Password *
            </label>
            <input
              className={inputClass}
              placeholder="Min. 6 characters"
              type="password"
              value={localPassword}
              onChange={(e) => {
                setLocalPassword(e.target.value);
                setPassword(e.target.value);
              }}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/40 uppercase tracking-[0.08em] mb-2">
              Confirm Password *
            </label>
            <input
              className={inputClass}
              placeholder="Repeat password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
        </div>

        {/* Terms & Conditions checkbox */}
        <div className="flex items-start gap-3 mt-4 mb-4">
          <button
            type="button"
            onClick={() => setTermsAccepted(!termsAccepted)}
            className={`w-5 h-5 flex-shrink-0 mt-0.5 rounded-[5px] border transition-all duration-200 flex items-center justify-center
              ${termsAccepted
                ? "bg-emerald-500 border-emerald-500"
                : "bg-white/5 border-white/20 hover:border-emerald-400/50"
              }`}
          >
            {termsAccepted && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <p className="text-[13px] text-white/40 leading-relaxed">
            I agree to uCart's{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 font-semibold hover:text-emerald-300 hover:underline transition-colors"
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 font-semibold hover:text-emerald-300 hover:underline transition-colors"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>

        {/* Field error */}
        {fieldError && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-4">
            <span className="text-red-400 text-[13px] font-medium">{fieldError}</span>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full mt-2 bg-emerald-500 text-white font-syne text-[17px] font-bold rounded-xl py-[18px]
                     flex items-center justify-center gap-3 transition-all duration-200
                     shadow-[0_0_40px_rgba(16,185,129,0.25)]
                     ${loading
                       ? "opacity-70 cursor-not-allowed"
                       : "hover:bg-emerald-600 hover:-translate-y-[2px] hover:shadow-[0_0_60px_rgba(16,185,129,0.35)]"
                     }`}
        >
          {loading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Creating your store...
            </>
          ) : (
            <>
              Create My Free Store
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>

        {/* Trust line */}
        <div className="flex items-center justify-center gap-2 mt-5 text-[12px] text-white/35">
          <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Free forever · No credit card · Account created instantly
        </div>

        {/* Already have account */}
        <p className="text-center text-[13px] text-white/35 mt-4">
          Already have an account?{" "}
          <a href="/Login" className="text-emerald-400 font-semibold hover:underline">
            Sign in here
          </a>
        </p>
      </div>
    </>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function Landing({
  handleAuth,
  email,
  setEmail,
  setPassword,
  isSignUp,
  setIsSignUp,
}) {
  const [showFloat, setShowFloat] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowFloat(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-[#0A0F0D] text-white overflow-x-hidden min-h-screen">

      {/* Global styles */}
      <style>{`
        @keyframes ticker   { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes fadeUp   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shake    { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
        @keyframes popIn    { 0%{transform:scale(0.5);opacity:0} 70%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
        @keyframes spin     { to{transform:rotate(360deg)} }
        select option       { background:#0A0F0D; }
        html                { scroll-behavior:smooth; }
      `}</style>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="min-h-screen flex flex-col items-center justify-center pt-20 pb-0 px-6 relative overflow-hidden text-center">

        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(16,185,129,0.14)_0%,transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_40%_40%_at_80%_80%,rgba(16,185,129,0.06)_0%,transparent_60%)]" />
          <div className="absolute inset-0 opacity-[0.035]"
            style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
        </div>

        {/* Badge */}
        <div className="relative z-10 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30
                        rounded-full px-5 py-2 text-[13px] font-bold text-emerald-400 mb-8"
          style={{ animation: "fadeUp 0.6s ease both" }}>
          <span className="w-2 h-2 bg-emerald-400 rounded-full" style={{ animation: "pulse 2s infinite" }} />
          Nigeria's free marketplace for university students
        </div>

        {/* H1 */}
        <h1 className="relative z-10 font-syne font-extrabold leading-[1.0] tracking-tight
                       text-[clamp(44px,7.5vw,88px)] mb-4"
          style={{ animation: "fadeUp 0.6s 0.1s ease both" }}>
          Your products.<br />
          <span className="text-emerald-400 relative">
            University buyers.
            <span className="absolute bottom-[-4px] left-0 right-0 h-[3px] bg-gradient-to-r from-emerald-400 to-transparent rounded-full" />
          </span>
        </h1>

        <p className="relative z-10 text-[18px] text-white/50 max-w-[520px] leading-[1.75] mt-6 mb-10"
          style={{ animation: "fadeUp 0.6s 0.2s ease both" }}>
          uCart is a <strong className="text-white/85 font-semibold">free Jumia-style marketplace</strong> built
          for Nigerian university students. Buyers checkout directly on site —
          and sellers pay <strong className="text-white/85 font-semibold">absolutely nothing</strong> to get started.
        </p>

        {/* CTAs */}
        <div className="relative z-10 flex items-center gap-4 flex-wrap justify-center mb-16"
          style={{ animation: "fadeUp 0.6s 0.3s ease both" }}>
          <button onClick={() => scrollTo("signup")}
            className="inline-flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-600 text-white
                       px-8 py-4 rounded-xl text-[16px] font-bold font-syne transition-all duration-200
                       hover:-translate-y-[2px] shadow-[0_0_40px_rgba(16,185,129,0.28)]
                       hover:shadow-[0_0_60px_rgba(16,185,129,0.38)]">
            Start Selling Free
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
          <button onClick={() => scrollTo("how")}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white px-6 py-4 rounded-xl
                       text-[15px] font-medium border border-white/10 hover:border-white/25
                       hover:bg-white/5 transition-all duration-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" /><path d="M10 8l4 4-4 4" />
            </svg>
            See how it works
          </button>
        </div>

        {/* Ticker at bottom of hero */}
        <div className="relative z-10 w-full">
          <Ticker />
        </div>
      </section>

      {/* ── STATS BAR ───────────────────────────────────────────────────────── */}
      <div className="border-b border-emerald-500/10 px-6 py-10">
        <RevealDiv className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-10 md:gap-20">
          {[
            { val: "400+",   label: "Active University Buyers" },
            { val: "218+",   label: "Products Listed" },
            { val: "0",      label: "NGN Cost to Start", prefix: "NGN " },
            { val: "5 min",  label: "Setup Time" },
            { val: "100%",   label: "Free Forever" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-syne text-[32px] font-extrabold text-emerald-400 leading-none">
                {s.prefix && <span className="text-white/40 text-[20px]">{s.prefix}</span>}
                {s.val}
              </div>
              <div className="text-[11px] text-white/35 font-semibold uppercase tracking-[0.08em] mt-1.5">
                {s.label}
              </div>
            </div>
          ))}
        </RevealDiv>
      </div>

      {/* ── WHY UCART ───────────────────────────────────────────────────────── */}
      <section id="why" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <SectionTag>Why uCart</SectionTag>
          <h2 className="font-syne text-[clamp(30px,4vw,52px)] font-extrabold leading-[1.1] tracking-tight mb-5">
            Mini Jumia. Completely free.<br />
            <span className="text-emerald-400">Built for student sellers.</span>
          </h2>
          <p className="text-[17px] text-white/45 max-w-xl leading-relaxed mb-14">
            Everything Jumia gets right — full site checkout, buyer protection, real orders —
            without the fees, delays, and corporate distance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CARDS.map((card, i) => <WhyCard key={i} card={card} delay={i * 80} />)}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────────────────── */}
      <section id="how" className="py-24 px-6 bg-white/[0.015]">
        <div className="max-w-[860px] mx-auto">
          <SectionTag>How it works</SectionTag>
          <h2 className="font-syne text-[clamp(30px,4vw,50px)] font-extrabold leading-[1.1] tracking-tight mb-4">
            Sign up to first sale<br />in 24 hours.
          </h2>
          <p className="text-[17px] text-white/45 mb-14">Four steps. Zero complexity.</p>
          {STEPS.map((step, i) => <Step key={i} step={step} index={i} />)}
        </div>
      </section>

      {/* ── COMPARE ─────────────────────────────────────────────────────────── */}
      <section id="compare" className="py-24 px-6 text-center">
        <div className="max-w-6xl mx-auto">
          <SectionTag center>How we compare</SectionTag>
          <h2 className="font-syne text-[clamp(30px,4vw,50px)] font-extrabold leading-[1.1] tracking-tight mb-4">
            uCart vs the alternatives
          </h2>
          <p className="text-[17px] text-white/45 max-w-lg mx-auto mb-12">
            See why smart student vendors choose uCart over Jumia and Jiji.
          </p>
          <CompareTable />
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-white/[0.015]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <SectionTag center>Vendor stories</SectionTag>
            <h2 className="font-syne text-[clamp(30px,4vw,50px)] font-extrabold leading-[1.1] tracking-tight">
              Vendors love selling on uCart
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => <TestiCard key={i} t={t} delay={i * 100} />)}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-[700px] mx-auto">
          <div className="text-center mb-12">
            <SectionTag center>FAQ</SectionTag>
            <h2 className="font-syne text-[clamp(30px,4vw,50px)] font-extrabold leading-[1.1] tracking-tight">
              Questions answered.
            </h2>
          </div>
          {FAQS.map((faq, i) => <FAQItem key={i} faq={faq} />)}
        </div>
      </section>

      {/* ── SIGNUP SECTION ──────────────────────────────────────────────────── */}
      <section id="signup"
        className="py-24 px-6 border-t border-emerald-500/10
                   bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(16,185,129,0.09)_0%,transparent_70%)]">
        <div className="max-w-[620px] mx-auto text-center">
          <SectionTag center>Start selling</SectionTag>
          <h2 className="font-syne text-[clamp(30px,4vw,52px)] font-extrabold leading-[1.1] tracking-tight mb-4">
            Your Products. Live today.<br />
            <span className="text-emerald-400">Free forever.</span>
          </h2>
          <p className="text-[17px] text-white/45 mb-10">
            Join 218 vendors already selling to 400+ university students. Create your account in 2 minutes.
          </p>

          <RevealDiv className="bg-white/[0.03] border border-emerald-500/20 rounded-3xl p-10
                                shadow-[0_0_80px_rgba(16,185,129,0.07)]">
            <SellerSignupForm
              handleAuth={handleAuth}
              email={email}
              setEmail={setEmail}
              setPassword={setPassword}
              setIsSignUp={setIsSignUp}
            />
          </RevealDiv>
        </div>
      </section>

      {/* ── FLOATING CTA ────────────────────────────────────────────────────── */}
      <button
        onClick={() => scrollTo("signup")}
        className={`fixed bottom-8 left-1/2 bg-emerald-500 hover:bg-emerald-600 text-white
                   font-syne font-bold text-[15px] px-7 py-3.5 rounded-full
                   shadow-[0_8px_40px_rgba(16,185,129,0.38)] whitespace-nowrap
                   flex items-center gap-2.5 z-40 transition-all duration-500
                   ${showFloat ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ transform: `translateX(-50%) translateY(${showFloat ? "0" : "40px"})` }}
      >
        Start Selling Free →
      </button>
    </div>
  );
}